// What My Leads and a lead's page show, from the partner's own referrals (MOTHER-87):
// the stage words a partner reads, which section a lead is in, dates as the viewer's
// local dates, and the stage timeline. Plain functions, so the rules are tested without
// rendering; LeadPages.tsx draws them.
import type { StatusStep } from '@olytic/foundation';

import type { Referral } from './api';

/**
 * My Leads is `/`; a lead's page is `/leads/<id>`; the Refer a Company form is `/refer`
 * (MOTHER-88); My Profile is `/profile` (MOTHER-182).
 */
export const MY_LEADS_PATH = '/';
export const REFER_PATH = '/refer';
export const PROFILE_PATH = '/profile';

/** True for the Refer a Company form's path (a trailing slash too). */
export function isReferPath(pathname: string): boolean {
  return pathname === REFER_PATH || pathname === `${REFER_PATH}/`;
}

/** True for My Profile's path (a trailing slash too). */
export function isProfilePath(pathname: string): boolean {
  return pathname === PROFILE_PATH || pathname === `${PROFILE_PATH}/`;
}
const LEAD_PREFIX = '/leads/';

export function leadPath(id: string): string {
  return `${LEAD_PREFIX}${encodeURIComponent(id)}`;
}

/** The lead id a path names (`/leads/<id>`), or null for My Leads (any other path). */
export function leadIdOf(pathname: string): string | null {
  if (!pathname.startsWith(LEAD_PREFIX)) return null;
  const rest = pathname.slice(LEAD_PREFIX.length).replace(/\/$/, '');
  if (rest === '' || rest.includes('/')) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return rest;
  }
}

/** Open leads, in the order a lead moves through them. */
export const PIPELINE_STAGES = ['submitted', 'contacted', 'in_discussions'] as const;
/** Finished leads. */
export const CLOSED_STAGES = ['closed_won', 'closed_lost', 'not_a_fit'] as const;

const STAGE_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  contacted: 'Contacted',
  in_discussions: 'In Discussions',
  closed_won: 'Became a Client',
  closed_lost: 'Lost',
  not_a_fit: 'Not a Fit',
};

/** The words a partner reads for a stage; an unknown one reads as itself, never blank. */
export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage;
}

export function isClosed(stage: string): boolean {
  return (CLOSED_STAGES as readonly string[]).includes(stage);
}

/**
 * Pipeline and Closed, each newest first. Every lead is in exactly one: a closed stage
 * goes to Closed, anything else (the three open stages, or one this page does not know)
 * stays in Pipeline, so no lead ever disappears from the page.
 */
export function splitLeads(referrals: Referral[]): { pipeline: Referral[]; closed: Referral[] } {
  const newestFirst = [...referrals].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || a.id.localeCompare(b.id));
  return { pipeline: newestFirst.filter((r) => !isClosed(r.stage)), closed: newestFirst.filter((r) => isClosed(r.stage)) };
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** A timestamp as the viewer's local date ("Oct 1, 2026" in en-US); empty when there is none. */
export function localDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const time = new Date(iso);
  return Number.isNaN(time.getTime()) ? '' : time.toLocaleDateString(undefined, DATE_FORMAT);
}

/** A calendar day (`YYYY-MM-DD`) as that same day, in the viewer's format: never shifted a day by their time zone. */
export function calendarDate(day: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '');
  if (!match) return '';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(undefined, DATE_FORMAT);
}

/** A referral rate as the partner reads it: `10%`, `12.5%`. */
export function percent(rate: number): string {
  return `${rate.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

/** "Riley Referral", or the email, or "Unnamed lead" when nothing names them. */
export function personName(referral: Referral): string {
  const name = [referral.firstName, referral.lastName].filter((part): part is string => typeof part === 'string' && part.trim() !== '').join(' ');
  return name || referral.email || 'Unnamed lead';
}

/**
 * The stage timeline: each stage the lead reached, with its date, in order, the current
 * one (its latest entry for the lead's stage) marked. A history that does not reach the
 * current stage (or none) ends with it, dated when it changed.
 */
export function stageSteps(referral: Referral): StatusStep[] {
  const history = [...referral.stages];
  if (!history.some((entry) => entry.stage === referral.stage)) {
    history.push({ stage: referral.stage, at: referral.stageChangedAt ?? referral.createdAt });
  }
  let current = -1;
  history.forEach((entry, index) => {
    if (entry.stage === referral.stage) current = index;
  });
  return history.map((entry, index) => ({
    key: `${index}-${entry.stage}`,
    label: stageLabel(entry.stage),
    state: index === current ? 'current' : 'done',
    at: localDate(entry.at),
  }));
}
